import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Col,
  Container,
  Row,
  Table,
  Button as BootstrapButton,
  Modal,
  Form,
} from "react-bootstrap";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import DateInput from "../../components/ui/DateInput";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import warehouseIcon from "../../media/icons8-hierarchy.png";
import GetData from "../../services/getData";
import filterIcon from "../../media/filter_icon.png";

function WarehouseUnits() {
  const { t } = useTranslation("warehouse");
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState("all");
  const [selectedService, setSelectedService] = useState("all");
  const [countFrom, setCountFrom] = useState("");
  const [countTo, setCountTo] = useState("");
  const [doseFrom, setDoseFrom] = useState("");
  const [doseTo, setDoseTo] = useState("");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceTo, setPriceTo] = useState("");

  const [isFiltersActive, setIsFiltersActive] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1,
  });

  const [showWriteoffModal, setShowWriteoffModal] = useState(false);
  const [writeoffUnit, setWriteoffUnit] = useState(null);
  const [writeoffCount, setWriteoffCount] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [unitForm, setUnitForm] = useState({
    id_warehouse: "",
    id_service: "",
    name: "",
    count: "",
    dose: "",
    expiration_date: "",
    purchase_price: "",
  });

  useEffect(() => {
    document.title = t("units.title");
  }, [t]);

  const getFieldValue = (field, fallback = "") => {
    if (field && typeof field === "object" && "value" in field) {
      return field.value;
    }
    return field ?? fallback;
  };

  const getNumericFieldValue = (field) => {
    const raw = getFieldValue(field, 0);
    const num = Number(raw);
    // NaN приводим к 0, чтобы корректно сравнивать и фильтровать
    return Number.isNaN(num) ? 0 : num;
  };

  const loadUnits = async () => {
    const timer = setTimeout(() => setLoading(true), 300);
    setError(null);
    try {
      const data = await GetData({
        module: "warehouse",
        block: "units",
        search: searchTerm.trim(),
        id_warehouse: selectedWarehouse === "all" ? "" : selectedWarehouse,
        id_service: selectedService === "all" ? "" : selectedService,
        count_from: countFrom || "",
        count_to: countTo || "",
        dose_from: doseFrom || "",
        dose_to: doseTo || "",
        price_from: priceFrom || "",
        price_to: priceTo || "",
        page: pagination.page,
        limit: pagination.limit,
      });

      const result = data.result || data.request?.result || [];
      const warehousesData =
        data.warehouses || data.request?.warehouses || [];
      const servicesData = data.services || data.request?.services || [];
      const paginationData =
        data.pagination || data.request?.pagination || null;

      // На фронте дополнительно отфильтровываем позиции с количеством <= 0
      const filtered = (result || []).filter((item) => {
        const count = getNumericFieldValue(item?.count);
        return count > 0;
      });

      setItems(filtered);
      setWarehouses(warehousesData);
      setServices(servicesData);

      if (paginationData) {
        setPagination((prev) => ({
          ...prev,
          page: paginationData.page ?? prev.page,
          limit: paginationData.limit ?? prev.limit,
          total: paginationData.total ?? prev.total,
          pages: paginationData.pages ?? prev.pages,
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Ошибка загрузки товарных единиц:", err);
      setError(err.message || t("units.errors.loadFailed"));
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      loadUnits();
    }, 400);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    selectedWarehouse,
    selectedService,
    countFrom,
    countTo,
    doseFrom,
    doseTo,
    priceFrom,
    priceTo,
    pagination.page,
  ]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleServiceChange = (e) => {
    setSelectedService(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleNumericFilterChange = (setter) => (e) => {
    setter(e.target.value.replace(/[^\d]/g, ""));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedWarehouse("all");
    setSelectedService("all");
    setCountFrom("");
    setCountTo("");
    setDoseFrom("");
    setDoseTo("");
    setPriceFrom("");
    setPriceTo("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const totalPages =
    pagination.pages ||
    (pagination.total && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : 1);

  const handlePageClick = (pageNumber) => {
    setPagination((prev) => ({ ...prev, page: pageNumber }));
  };

  const renderPageButtons = () => {
    if (totalPages <= 1) return null;
    if (totalPages < 5) {
      return Array.from({ length: totalPages }, (_, idx) => {
        const pageNum = idx + 1;
        return (
          <BootstrapButton
            key={pageNum}
            variant={pageNum === pagination.page ? "primary" : "outline-secondary"}
            size="sm"
            className="mx-1"
            onClick={() => handlePageClick(pageNum)}
          >
            {pageNum}
          </BootstrapButton>
        );
      });
    }
    const currentPage = pagination.page;
    let start = Math.max(1, currentPage - 1);
    let end = start + 2;
    if (end > totalPages) {
      end = totalPages;
      start = end - 2;
    }
    const buttons = [];
    if (start > 1) {
      buttons.push(
        <BootstrapButton
          key="left-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(start - 1)}
        >
          ...
        </BootstrapButton>,
      );
    }
    for (let p = start; p <= end; p++) {
      buttons.push(
        <BootstrapButton
          key={p}
          variant={p === currentPage ? "primary" : "outline-secondary"}
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(p)}
        >
          {p}
        </BootstrapButton>,
      );
    }
    if (end < totalPages) {
      buttons.push(
        <BootstrapButton
          key="right-ellipsis"
          variant="outline-secondary"
          size="sm"
          className="mx-1"
          onClick={() => handlePageClick(end + 1)}
        >
          ...
        </BootstrapButton>,
      );
    }
    return buttons;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setUnitForm((prev) => ({
      ...prev,
      [name]:
        name === "count" || name === "dose" || name === "purchase_price"
          ? value.replace(/[^\d]/g, "")
          : value,
    }));
  };

  const openAddModal = () => {
    const defaultWarehouseId =
      selectedWarehouse !== "all" && selectedWarehouse
        ? selectedWarehouse
        : warehouses[0]?.id?.value || warehouses[0]?.id || "";
    const defaultServiceId =
      selectedService !== "all" && selectedService
        ? selectedService
        : services[0]?.id?.value || services[0]?.id || "";

    setEditingUnit(null);
    setUnitForm({
      id_warehouse: String(defaultWarehouseId ?? ""),
      id_service: String(defaultServiceId ?? ""),
      name: "",
      count: "",
      dose: "",
      expiration_date: "",
      purchase_price: "",
    });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    const idValue = item?.id?.value || item?.id;
    const warehouseValue =
      item?.id_warehouse?.value || item?.id_warehouse || "";
    const serviceValue = item?.id_service?.value || item?.id_service || "";

    setEditingUnit({
      id: idValue,
    });

    setUnitForm({
      id_warehouse: String(warehouseValue ?? ""),
      id_service: String(serviceValue ?? ""),
      name: item?.name?.value || item?.name || "",
      count: String(item?.count?.value || item?.count || ""),
      dose: String(item?.dose?.value || item?.dose || ""),
      expiration_date:
        item?.expiration_date?.value ||
        item?.expiration_date ||
        "",
      purchase_price: String(
        item?.purchase_price?.value || item?.purchase_price || "",
      ),
    });
    setShowModal(true);
  };

  const openWriteoffModal = (item, e) => {
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
    const currentCount = Number(item?.count?.value || item?.count || 0);
    setWriteoffUnit({
      id: item?.id?.value || item?.id,
      name: item?.name?.value || item?.name || "",
      count: currentCount,
    });
    setWriteoffCount("");
    setShowWriteoffModal(true);
  };

  const handleWriteoffClose = () => {
    setShowWriteoffModal(false);
    setWriteoffUnit(null);
    setWriteoffCount("");
  };

  const handleWriteoffChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    setWriteoffCount(value);
  };

  const handleWriteoffSubmit = async () => {
    if (!writeoffUnit || !writeoffUnit.id) {
      return;
    }
    const requested = Number(writeoffCount || 0);
    const maxCount = Number(writeoffUnit.count || 0);

    if (!requested || requested <= 0) {
      // eslint-disable-next-line no-alert
      alert(t("writeoff.errors.needCount"));
      return;
    }

    if (requested > maxCount) {
      // eslint-disable-next-line no-alert
      alert(t("writeoff.errors.tooMany"));
      return;
    }

    try {
      await GetData({
        module: "warehouse",
        block: "units",
        action: "writeoff",
        id: writeoffUnit.id,
        writeoff_count: requested,
      });
      await loadUnits();
      handleWriteoffClose();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(t("writeoff.errors.failed"));
      // eslint-disable-next-line no-console
      console.error("Ошибка списания товарной единицы:", err);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handleSaveUnit = async () => {
    if (
      !unitForm.id_warehouse ||
      !unitForm.id_service ||
      !unitForm.name.trim() ||
      !unitForm.count ||
      !unitForm.dose ||
      !unitForm.purchase_price
    ) {
      // eslint-disable-next-line no-alert
      alert(t("units.errors.fillAll"));
      return;
    }

    try {
      await GetData({
        module: "warehouse",
        block: "units",
        action: editingUnit ? "edit" : "add",
        id: editingUnit?.id,
        id_warehouse: unitForm.id_warehouse,
        id_service: unitForm.id_service,
        name: unitForm.name.trim(),
        count: unitForm.count,
        dose: unitForm.dose,
        expiration_date: unitForm.expiration_date || "",
        purchase_price: unitForm.purchase_price,
      });
      await loadUnits();
      setShowModal(false);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(t("units.errors.saveFailed"));
      // eslint-disable-next-line no-console
      console.error("Ошибка сохранения товарной единицы:", err);
    }
  };

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={warehouseIcon}
                title={t("units.title")}
                showStatus={false}
                status=""
              />
            }
            buttons={[
              {
                label: t("units.add"),
                onClick: openAddModal,
                type: "normal",
              },
            ]}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""}`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">{t("filters.title", { ns: "common" })}</div>
                    </div>
                    <button
                      type="button"
                      className="button-default light"
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                    >
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </button>
                  </div>
                  <div className="search-section">
                    <label>{t("filters.searchLabel", { ns: "common" })}</label>
                    <input
                      type="text"
                      placeholder={t("units.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="search-input"
                    />
                  </div>
                  <div className="filters-section">
                    <div className="filter-group">
                      <label>{t("shared.warehouseLabel")}</label>
                      <select
                        value={selectedWarehouse}
                        onChange={handleWarehouseChange}
                      >
                        <option value="all">{t("shared.allWarehouses")}</option>
                        {warehouses.map((warehouse) => (
                          <option
                            key={warehouse?.id?.value || warehouse?.id}
                            value={warehouse?.id?.value || warehouse?.id}
                          >
                            {warehouse?.name?.value || warehouse?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <label>{t("shared.serviceLabel")}</label>
                      <select
                        value={selectedService}
                        onChange={handleServiceChange}
                      >
                        <option value="all">{t("shared.allServices")}</option>
                        {services.map((service) => (
                          <option
                            key={service?.id?.value || service?.id}
                            value={service?.id?.value || service?.id}
                          >
                            {service?.name?.value || service?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group filter-range">
                      <label>{t("units.countLabel")}</label>
                      <div className="filter-range-inputs">
                        <input
                          type="text"
                          placeholder={t("shared.from")}
                          value={countFrom}
                          onChange={handleNumericFilterChange(setCountFrom)}
                        />
                        <span className="range-separator">—</span>
                        <input
                          type="text"
                          placeholder={t("shared.to")}
                          value={countTo}
                          onChange={handleNumericFilterChange(setCountTo)}
                        />
                      </div>
                    </div>
                    <div className="filter-group filter-range">
                      <label>{t("units.doseLabel")}</label>
                      <div className="filter-range-inputs">
                        <input
                          type="text"
                          placeholder={t("shared.from")}
                          value={doseFrom}
                          onChange={handleNumericFilterChange(setDoseFrom)}
                        />
                        <span className="range-separator">—</span>
                        <input
                          type="text"
                          placeholder={t("shared.to")}
                          value={doseTo}
                          onChange={handleNumericFilterChange(setDoseTo)}
                        />
                      </div>
                    </div>
                    <div className="filter-group filter-range">
                      <label>{t("units.priceLabel")}</label>
                      <div className="filter-range-inputs">
                        <input
                          type="text"
                          placeholder={t("shared.from")}
                          value={priceFrom}
                          onChange={handleNumericFilterChange(setPriceFrom)}
                        />
                        <span className="range-separator">—</span>
                        <input
                          type="text"
                          placeholder={t("shared.to")}
                          value={priceTo}
                          onChange={handleNumericFilterChange(setPriceTo)}
                        />
                      </div>
                    </div>
                    <button
                      className="button-default light"
                      onClick={clearFilters}
                    >
                      {t("filters.reset", { ns: "common" })}
                    </button>
                  </div>
                </div>
              </div>

              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("state.loading", { ns: "common" })}</div>
                ) : items.length === 0 ? (
                  <div className="no-patients">
                    {t("units.notFound")}
                  </div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("units.colName")}</th>
                        <th>{t("units.colWarehouse")}</th>
                        <th>{t("units.colService")}</th>
                        <th>{t("units.colCount")}</th>
                        <th>{t("units.colDose")}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr
                          key={item?.id?.value || item?.id}
                          className="table-item-link"
                          onClick={() => openEditModal(item)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>{getFieldValue(item?.name, "")}</td>
                          <td>
                            {getFieldValue(item?.warehouse_name, "")}
                          </td>
                          <td>
                            {getFieldValue(item?.service_name, "")}
                          </td>
                          <td>{getNumericFieldValue(item?.count)}</td>
                          <td>{getNumericFieldValue(item?.dose)}</td>
                          <td className="text-end">
                            <BootstrapButton
                              variant="outline-danger"
                              size="sm"
                              onClick={(e) => openWriteoffModal(item, e)}
                            >
                              {t("units.writeoff")}
                            </BootstrapButton>
                          </td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={6} className="summary-cell">
                          {t("shared.foundItems", { count: pagination.total || items.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
                {items.length > 0 && totalPages > 1 && (
                  <div className="pagination-controls mt-3 d-flex justify-content-between align-items-center">
                    <span>
                      {t("shared.pageOf", { page: pagination.page, total: totalPages })}
                    </span>
                    <div>{renderPageButtons()}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Modal show={showModal} onHide={handleModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUnit ? t("units.editTitle") : t("units.addTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.warehouse")}</Form.Label>
              <Form.Select
                name="id_warehouse"
                value={unitForm.id_warehouse}
                onChange={handleFormChange}
              >
                <option value="">{t("units.form.selectWarehouse")}</option>
                {warehouses.map((warehouse) => (
                  <option
                    key={warehouse?.id?.value || warehouse?.id}
                    value={warehouse?.id?.value || warehouse?.id}
                  >
                    {warehouse?.name?.value || warehouse?.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.service")}</Form.Label>
              <Form.Select
                name="id_service"
                value={unitForm.id_service}
                onChange={handleFormChange}
              >
                <option value="">{t("units.form.selectService")}</option>
                {services.map((service) => (
                  <option
                    key={service?.id?.value || service?.id}
                    value={service?.id?.value || service?.id}
                  >
                    {service?.name?.value || service?.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.name")}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={unitForm.name}
                onChange={handleFormChange}
                placeholder={t("units.form.namePlaceholder")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.dose")}</Form.Label>
              <Form.Control
                type="text"
                name="dose"
                value={unitForm.dose}
                onChange={handleFormChange}
                placeholder={t("units.form.dosePlaceholder")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.count")}</Form.Label>
              <Form.Control
                type="text"
                name="count"
                value={unitForm.count}
                onChange={handleFormChange}
                placeholder={t("units.form.countPlaceholder")}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.expiration")}</Form.Label>
              <DateInput
                className="form-control"
                name="expiration_date"
                value={unitForm.expiration_date}
                onChange={handleFormChange}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t("units.form.price")}</Form.Label>
              <Form.Control
                type="text"
                name="purchase_price"
                value={unitForm.purchase_price}
                onChange={handleFormChange}
                placeholder={t("units.form.pricePlaceholder")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={handleModalClose}>
            {t("actions.cancel", { ns: "common" })}
          </BootstrapButton>
          <BootstrapButton
            variant="primary"
            onClick={handleSaveUnit}
            disabled={
              !unitForm.id_warehouse ||
              !unitForm.id_service ||
              !unitForm.name.trim() ||
              !unitForm.count ||
              !unitForm.dose ||
              !unitForm.purchase_price
            }
          >
            {t("actions.save", { ns: "common" })}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={showWriteoffModal} onHide={handleWriteoffClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("writeoff.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {writeoffUnit && (
            <>
              <p>
                {t("writeoff.item")} <strong>{writeoffUnit.name}</strong>
              </p>
              <p>{t("writeoff.inStock", { count: writeoffUnit.count })}</p>
            </>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                {writeoffUnit
                  ? t("writeoff.countWithMax", { count: writeoffUnit.count })
                  : t("writeoff.countLabel")}
              </Form.Label>
              <Form.Control
                type="text"
                value={writeoffCount}
                onChange={handleWriteoffChange}
                placeholder={t("writeoff.countPlaceholder")}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={handleWriteoffClose}>
            {t("actions.cancel", { ns: "common" })}
          </BootstrapButton>
          <BootstrapButton
            variant="danger"
            onClick={handleWriteoffSubmit}
            disabled={
              !writeoffUnit ||
              !writeoffCount ||
              Number(writeoffCount) <= 0 ||
              Number(writeoffCount) >
                Number(writeoffUnit?.count || 0)
            }
          >
            {t("writeoff.submit")}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default WarehouseUnits;

